import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import NetworkApi from "../../services/networks";
import CreateNetworkForm from "../../forms/CreateNetworkForm"
import CountryApi from "../../services/countries";
import StateApi from "../../services/states";
import MunicipalityApi from "../../services/municipalities";
import Error from "../../fragments/form/Error"
import Message from "../../fragments/form/Message"

function EditNetworkPage(props) {
    const { networkId } = useParams();
    const navigate = useNavigate();
    const [network, setNetwork] = useState(null);
    const [location, setLocation] = useState({
        address_1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        zip_code: '',
        latitude: null,
        longitude: null,
        country_id: null,
        state_id: null,
        city_id: null
    });
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    
    const networkApi = useMemo(() => new NetworkApi(props.token), [props.token]);
    const countryApi = useMemo(() => new CountryApi(props.token), [props.token]);
    const stateApi = useMemo(() => new StateApi(props.token), [props.token]);
    const municipalityApi = useMemo(() => new MunicipalityApi(props.token), [props.token]);

    const loadNetwork = useCallback(async () => {
        try {
            const response = await networkApi.getById(networkId);
            setNetwork(response);
            
            // Set location data if available
            if (response) {
                setLocation({
                    address_1: response.address || '',
                    address2: response.address2 || '',
                    city: response.city || '',
                    state: response.state || '',
                    country: response.country || '',
                    zip_code: response.zip_code || '',
                    latitude: response.latitude || null,
                    longitude: response.longitude || null,
                    country_id: response.country_id || null,
                    state_id: response.state_id || null,
                    city_id: response.city_id || null
                });
            }
        } catch (error) {
            console.error("Error loading network:", error);
            setError("Failed to load network");
        }
    }, [networkId, networkApi]);

    const loadCountries = useCallback(async () => {
        try {
            const response = await countryApi.all();
            // Format countries for SearchSelector: {label: country_name, value: id}
            const formattedCountries = (response.countries || []).map(country => ({
                label: country.country,
                value: country.id,
                ...country
            }));
            setCountries(formattedCountries);
        } catch (error) {
            console.error("Error loading countries:", error);
        }
    }, [countryApi]);

    useEffect(() => {
        loadNetwork();
        loadCountries();
    }, [networkId, loadNetwork, loadCountries]);

    const handleSearchLocation = async (id, countryId, stateId, search) => {
        try {
            if (id === 'country') {
                const response = await countryApi.search(search);
                // Format countries for SearchSelector: {label: country_name, value: id}
                const formattedCountries = (response.countries || []).map(country => ({
                    label: country.country,
                    value: country.id,
                    ...country
                }));
                setCountries(formattedCountries);
            } else if (id === 'state' && countryId) {
                const response = await stateApi.search(countryId, search);
                // Format states for SearchSelector: {label: state_name, value: id}
                const formattedStates = (response.states || []).map(state => ({
                    label: state.state,
                    value: state.id,
                    ...state
                }));
                setStates(formattedStates);
            } else if (id === 'city' && stateId) {
                const response = await municipalityApi.search(stateId, search);
                // Format cities for SearchSelector: {label: city_name, value: id}
                const formattedCities = (response.cities || []).map(city => ({
                    label: city.city,
                    value: city.id,
                    ...city
                }));
                setCities(formattedCities);
            }
        } catch (error) {
            console.error("Error searching location:", error);
        }
    };

    const handleSetLocation = (newLocation) => {
        setLocation(newLocation);
    };

    const getErrorMessage = (error) => {
        // Network errors (no response from server)
        if (!error.response) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                return "Network connection error. Please check your internet connection and try again.";
            }
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                return "Request timed out. The server is taking too long to respond. Please try again.";
            }
            if (error.code === 'ERR_INTERNET_DISCONNECTED') {
                return "No internet connection. Please check your network settings.";
            }
            return "Network error occurred. Please check your connection and try again.";
        }

        // API errors (server responded with error status)
        const status = error.response?.status;
        const data = error.response?.data;

        if (status === 400) {
            // Validation errors
            const message = data?.message || data?.error || "Invalid data provided. Please check your input.";
            return message;
        }
        if (status === 401) {
            return "Authentication failed. Please log in again.";
        }
        if (status === 403) {
            return "You don't have permission to update this network.";
        }
        if (status === 404) {
            return "Network not found. It may have been deleted.";
        }
        if (status === 409) {
            return data?.message || "A network with this subdomain or domain already exists.";
        }
        if (status === 413) {
            return "File too large. Please reduce the image size and try again.";
        }
        if (status >= 500) {
            return "Server error occurred. Please try again later or contact support.";
        }

        // Generic API error
        return data?.message || data?.error || `Error: ${status} - ${error.message}`;
    };

    const onSubmit = async (formData, locationData) => {
        try {
            // Add location data to form data
            if (locationData) {
                Object.keys(locationData).forEach(key => {
                    if (locationData[key] !== null && locationData[key] !== '') {
                        // Map address_1 to address for API compatibility
                        const apiKey = key === 'address_1' ? 'address' : key;
                        formData.set(apiKey, locationData[key]);
                    }
                });
            }

            const response = await networkApi.update(networkId, formData);
            
            if (response && response.id) {
                setMessage("Network updated successfully!");
                setError(null);
                setTimeout(() => {
                    navigate(`/network/${networkId}`);
                }, 2000);
            } else {
                setError("Failed to update network - no response from server");
                setMessage(null);
            }
        } catch (error) {
            console.error("Error updating network:", error);
            const errorMessage = getErrorMessage(error);
            setError(errorMessage);
            setMessage(null);
        }
    };

    if (!network) {
        return (
            <div className='dashboard-content'>
                <div className='dashboard-content-container'>
                    <div className='dashboard-content-header'>
                        <h2>Loading Network...</h2>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='dashboard-content'>
            <div className='dashboard-content-container'>
                <div className='dashboard-content-header'>
                    <h2>Edit Network: {network.name}</h2>
                    <div>
                        <button 
                            className="btn btn-secondary"
                            onClick={() => navigate(`/network/${networkId}`)}
                        >
                            Back to Network
                        </button>
                    </div>
                </div>
                <Error message={error} />
                <Message message={message} />
                <CreateNetworkForm
                    onSubmit={onSubmit}
                    location={location}
                    network={network}
                    handleSetLocation={handleSetLocation}
                    handleSearchLocation={handleSearchLocation}
                    countries={countries}
                    states={states}
                    cities={cities}
                    {...props}
                />
            </div>
        </div>
    )
}

export default EditNetworkPage
